import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';

/**
 * Hook for Web Tracking data: sites, analytics, visitors, visitor history.
 * Follows the same pattern as useSignalData.
 */
export function useWebTrackingData() {
  const { currentUser } = useAuth();
  const { isAdminOrHigher, hasPermission, assignedCampaigns } = useRBAC();

  // Campaign filter: null = admin sees all, array = moderator sees only assigned
  const campaignFilter = isAdminOrHigher ? null : (assignedCampaigns?.length > 0 ? assignedCampaigns : []);

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [visitorHistory, setVisitorHistory] = useState(null);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [visitorsLoading, setVisitorsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);

  const reqId = useRef(0);

  // ─── Fetch user's accessible sites ───
  const fetchSites = useCallback(async () => {
    if (!currentUser?.email) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('tracked_sites')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;
      setSites(data || []);

      // Auto-select first site if none selected
      if (!selectedSite && data && data.length > 0) {
        setSelectedSite(data[0]);
      }
    } catch (err) {
      console.error('fetchSites error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.email, selectedSite]);

  // ─── Fetch analytics for selected site ───
  const fetchAnalytics = useCallback(async (days = 30) => {
    if (!selectedSite?.id) {
      setAnalytics(null);
      return;
    }

    const thisReq = ++reqId.current;
    setAnalyticsLoading(true);
    try {
      const rpcParams = {
        p_site_id: selectedSite.id,
        p_days: days,
      };
      if (campaignFilter !== null) rpcParams.p_campaign_names = campaignFilter;
      const { data, error: err } = await supabase.rpc('get_site_analytics', rpcParams);
      if (thisReq !== reqId.current) return;
      if (err) throw err;
      setAnalytics(data);
    } catch (err) {
      if (thisReq === reqId.current) {
        console.error('fetchAnalytics error:', err);
        setError(err.message);
      }
    } finally {
      if (thisReq === reqId.current) setAnalyticsLoading(false);
    }
  }, [selectedSite?.id, campaignFilter]);

  // ─── Fetch paginated visitors for selected site ───
  const fetchVisitors = useCallback(async ({
    page = 1,
    pageSize = 20,
    sortBy = 'last_visit',
    sortOrder = 'desc',
  } = {}) => {
    if (!selectedSite?.id) {
      setVisitors(null);
      return;
    }

    setVisitorsLoading(true);
    try {
      const rpcParams = {
        p_site_id: selectedSite.id,
        p_page: page,
        p_page_size: pageSize,
        p_sort_by: sortBy,
        p_sort_order: sortOrder,
      };
      if (campaignFilter !== null) rpcParams.p_campaign_names = campaignFilter;
      const { data, error: err } = await supabase.rpc('get_site_visitors', rpcParams);
      if (err) throw err;
      setVisitors(data);
    } catch (err) {
      console.error('fetchVisitors error:', err);
      setError(err.message);
    } finally {
      setVisitorsLoading(false);
    }
  }, [selectedSite?.id, campaignFilter]);

  // ─── Fetch visitor history (per lead) ───
  const fetchVisitorHistory = useCallback(async (leadEmail) => {
    if (!selectedSite?.id || !leadEmail) {
      setVisitorHistory(null);
      return;
    }

    setHistoryLoading(true);
    try {
      const rpcParams = {
        p_site_id: selectedSite.id,
        p_lead_email: leadEmail,
      };
      if (campaignFilter !== null) rpcParams.p_campaign_names = campaignFilter;
      const { data, error: err } = await supabase.rpc('get_visitor_history', rpcParams);
      if (err) throw err;
      setVisitorHistory(data);
      setSelectedVisitor(leadEmail);
    } catch (err) {
      console.error('fetchVisitorHistory error:', err);
      setError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedSite?.id, campaignFilter]);

  // ─── CRUD: Add site ───
  const addSite = useCallback(async ({ domain, siteName }) => {
    if (!currentUser?.email) throw new Error('Not authenticated');

    const cleanDomain = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');

    const { data, error: err } = await supabase
      .from('tracked_sites')
      .insert({
        domain: cleanDomain,
        site_name: siteName.trim(),
        owner_email: currentUser.email.toLowerCase(),
      })
      .select()
      .single();

    if (err) {
      if (err.code === '23505' && err.message?.includes('domain')) {
        throw new Error(`Domain "${cleanDomain}" is already being tracked.`);
      }
      throw err;
    }

    setSites(prev => [data, ...prev]);
    if (!selectedSite) setSelectedSite(data);
    return data;
  }, [currentUser?.email, selectedSite]);

  // ─── CRUD: Update site ───
  const updateSite = useCallback(async (siteId, updates) => {
    const { data, error: err } = await supabase
      .from('tracked_sites')
      .update(updates)
      .eq('id', siteId)
      .select()
      .single();

    if (err) throw err;

    setSites(prev => prev.map(s => s.id === siteId ? data : s));
    if (selectedSite?.id === siteId) setSelectedSite(data);
    return data;
  }, [selectedSite?.id]);

  // ─── CRUD: Remove site ───
  const removeSite = useCallback(async (siteId) => {
    const { error: err } = await supabase
      .from('tracked_sites')
      .delete()
      .eq('id', siteId);

    if (err) throw err;

    setSites(prev => prev.filter(s => s.id !== siteId));
    if (selectedSite?.id === siteId) {
      setSelectedSite(null);
      setAnalytics(null);
      setVisitors(null);
    }
  }, [selectedSite?.id]);

  // ─── Toggle active ───
  const toggleSiteActive = useCallback(async (siteId) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) return;
    return updateSite(siteId, { is_active: !site.is_active });
  }, [sites, updateSite]);

  // ─── GDPR: Delete visitor data ───
  const deleteVisitorData = useCallback(async (leadEmail) => {
    const { data, error: err } = await supabase.rpc('delete_visitor_tracking_data', {
      p_lead_email: leadEmail,
    });
    if (err) throw err;
    return data;
  }, []);

  // ─── Site user management ───
  const addSiteUser = useCallback(async (siteId, userEmail, role = 'viewer') => {
    const { data, error: err } = await supabase
      .from('tracked_site_users')
      .insert({
        site_id: siteId,
        user_email: userEmail.toLowerCase().trim(),
        role,
        assigned_by: currentUser?.email?.toLowerCase(),
      })
      .select()
      .single();

    if (err) {
      if (err.code === '23505') {
        throw new Error(`User "${userEmail}" already has access to this site.`);
      }
      throw err;
    }
    return data;
  }, [currentUser?.email]);

  const removeSiteUser = useCallback(async (siteUserId) => {
    const { error: err } = await supabase
      .from('tracked_site_users')
      .delete()
      .eq('id', siteUserId);

    if (err) throw err;
  }, []);

  const fetchSiteUsers = useCallback(async (siteId) => {
    const { data, error: err } = await supabase
      .from('tracked_site_users')
      .select('*')
      .eq('site_id', siteId)
      .order('created_at', { ascending: true });

    if (err) throw err;
    return data || [];
  }, []);

  // ─── Initial fetch ───
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // ─── Refetch analytics when site changes ───
  useEffect(() => {
    if (selectedSite?.id) {
      fetchAnalytics(30);
    }
  }, [selectedSite?.id, fetchAnalytics]);

  return {
    // State
    sites,
    selectedSite,
    setSelectedSite,
    analytics,
    visitors,
    visitorHistory,
    selectedVisitor,
    setSelectedVisitor,
    loading,
    analyticsLoading,
    visitorsLoading,
    historyLoading,
    error,

    // Data fetching
    fetchSites,
    fetchAnalytics,
    fetchVisitors,
    fetchVisitorHistory,

    // CRUD
    addSite,
    updateSite,
    removeSite,
    toggleSiteActive,

    // Site user management
    addSiteUser,
    removeSiteUser,
    fetchSiteUsers,

    // GDPR
    deleteVisitorData,
  };
}
